import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Box,
  Radio,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import API_BASE_URL from '../config/appConfig';
import { toLowerCaseEmail } from '../../utilities/email';

// Step Titles
const steps = ['Contact Details', 'Organisation Details', 'Select Plan'];

// Price Options
const priceOptions = [
  { type: 'Free', price: 0 , duration:1 ,users:20},
  { type: 'Pro', price: 50,duration:3,users:50 },
  { type: 'Max', price: 100,duration:6,users:100 },
];

// Validation Schemas
const orgSchema = Yup.object().shape({
  organisationName: Yup.string().required('*required'),
  organisationEmail: Yup.string().email('Invalid email').required('*required'),
  numberOfEmps:Yup.number().required('*required'),
  organisationLocation:Yup.string().required('*required'),
  Url:Yup.string().required('*required')
});

const userSchema = Yup.object().shape({
    FirstName:Yup.string().required('*required'),
    LastName:Yup.string().required('*required'),
    email: Yup.string().email('Invalid email').required('*required'),
    mobile: Yup.string()
  .matches(/^[6-9]\d{9}$/, 'Invalid mobile number') 
  .required('*required'),

})

const priceSchema = Yup.object().shape({
  planType: Yup.string().required('Please select a plan'),
});

// Enhanced MUI Styled Components (consistent with login/forgot-password)
const StyledStepper = styled(Stepper)(({ theme }) => ({
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '& .MuiStepLabel-label': {
    fontSize: '13px',
    fontWeight: 600,
    color: '#adb5bd',
    marginTop: theme.spacing(1),
    whiteSpace: 'nowrap',
    transition: 'color 0.2s ease',
    '&.Mui-active': {
      color: '#212529',
    },
    '&.Mui-completed': {
      color: '#60A630',
    },
  },
  '& .MuiStepIcon-root': {
    fontSize: '28px',
    color: '#e9ecef',
    transition: 'all 0.2s ease',
    '&.Mui-active': {
      color: '#60A630',
      filter: 'drop-shadow(0 3px 6px rgba(96, 166, 48, 0.35))',
    },
    '&.Mui-completed': {
      color: '#60A630',
    },
  },
  '& .MuiStepIcon-text': {
    fill: '#6c757d',
    fontWeight: 600,
    fontSize: '13px',
  },
  '& .Mui-active .MuiStepIcon-text': {
    fill: '#ffffff',
  },
  '& .MuiStepConnector-root': {
    top: '13px',
  },
  '& .MuiStepConnector-line': {
    borderColor: '#e9ecef',
    borderTopWidth: '2px',
    borderRadius: '2px',
    transition: 'border-color 0.2s ease',
  },
  '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
    borderColor: '#60A630',
  },
}));

const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    background: '#f7f8fb',
    fontSize: '14px',
    transition: `background .3s ${EASE}, box-shadow .3s ${EASE}`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e8eaf0',
      transition: `border-color .3s ${EASE}`,
    },
    '&:hover': {
      background: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cfd6e4',
      },
    },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: '0 0 0 4px rgba(0, 172, 86, 0.13)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#00ac56',
        borderWidth: '1.5px',
      },
    },
    '&.Mui-error.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(217, 45, 32, 0.12)',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    fontSize: '14px',
    color: '#7a8194',
    '&.Mui-focused': {
      color: '#059048',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    marginTop: theme.spacing(0.5),
    fontSize: '12px',
    fontWeight: 500,
    '&.Mui-error': {
      color: '#dc3545',
    },
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '14px',
  padding: theme.spacing(1.25, 4),
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  color: '#ffffff',
  background: 'linear-gradient(135deg, #00ac56 0%, #059048 100%)',
  boxShadow: '0 10px 22px -10px rgba(0, 172, 86, 0.85)',
  transition: `transform .3s ${EASE}, box-shadow .3s ${EASE}`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '-60%',
    width: '45%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.38), transparent)',
    transform: 'skewX(-18deg)',
    transition: `left .65s ${EASE}`,
  },
  '&:hover': {
    background: 'linear-gradient(135deg, #059048 0%, #03713a 100%)',
    boxShadow: '0 16px 30px -12px rgba(0, 172, 86, 0.95)',
    transform: 'translateY(-2px)',
    '&::after': { left: '120%' },
  },
  '&.Mui-disabled': {
    background: '#eef1f6',
    color: '#98a0b0',
    boxShadow: 'none',
    transform: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover': { transform: 'none' },
    '&::after': { display: 'none' },
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: '14px',
  padding: theme.spacing(1.25, 3),
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  color: '#344054',
  background: '#ffffff',
  border: '1.5px solid #e8eaf0',
  transition: `transform .3s ${EASE}, border-color .3s ${EASE}, box-shadow .3s ${EASE}`,
  '&:hover': {
    borderColor: 'rgba(0, 172, 86, 0.45)',
    color: '#059048',
    background: '#ffffff',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 24px -14px rgba(16, 24, 40, 0.55)',
  },
  '&:disabled': {
    borderColor: '#f1f3f5',
    color: '#ced4da',
    background: '#ffffff',
  },
}));

const PlanCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  flex: '1 1 0',
  minWidth: '150px',
  padding: theme.spacing(2, 2.25),
  borderRadius: '16px',
  cursor: 'pointer',
  background: selected ? 'rgba(0, 172, 86, 0.05)' : '#ffffff',
  border: selected ? '1.5px solid #00ac56' : '1.5px solid #e8eaf0',
  boxShadow: selected
    ? '0 14px 28px -16px rgba(0, 172, 86, 0.55)'
    : '0 1px 3px rgba(16, 24, 40, 0.05)',
  transition: `transform .3s ${EASE}, border-color .3s ${EASE}, box-shadow .3s ${EASE}, background .3s ${EASE}`,
  '&:hover': {
    borderColor: 'rgba(0, 172, 86, 0.55)',
    transform: 'translateY(-3px)',
    boxShadow: '0 16px 30px -16px rgba(0, 172, 86, 0.5)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover': { transform: 'none' },
  },
}));

const PlanDetail = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#6c757d',
  marginTop: '6px',
});

const RegistrationForm = ({ setError = () => {}, setSnackbarOpen = () => {} }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});

  const handleNext = (values) => {
    console.log(values ,'values')
    setFormData({ ...formData, ...values });
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async(data) => {
try{
const payload = {
  ...data,
  email: toLowerCaseEmail(data.email),
  organisationEmail: toLowerCaseEmail(data.organisationEmail),
};
let registerOrganisation = await axios.post(`${API_BASE_URL}/organisation/register` , payload)
console.log(registerOrganisation , 'registerOrganisation')

if(registerOrganisation.status=400){
  let responsess = await registerOrganisation.json()
  const errorMessage = await responsess.message;
  setError(errorMessage);
  setSnackbarOpen(true);
}
console.log(registerOrganisation , 'register from front end')
}
catch(error){

}


    console.log('Form Submitted:', data);
   
  };

  return (
    <Box sx={{ width: '100%', margin: '0 auto' }}>
    <StyledStepper activeStep={activeStep} alternativeLabel>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </StyledStepper>
    <Box sx={{ mt: 4 }}>
      {activeStep === 0 && (
        <Formik
          initialValues={{
            FirstName: "",
            LastName: "",
            email: "",
            mobile: "",
          }}
          validationSchema={userSchema}
          onSubmit={handleNext}
        >
          {({ errors, touched, handleChange, handleBlur, values }) => (
            <Form>
              <StyledTextField
                fullWidth
                label="First Name"
                name="FirstName"
                placeholder="Enter your first name"
                value={values.FirstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.FirstName && !!errors.FirstName}
                helperText={touched.FirstName && errors.FirstName}
              />
              <StyledTextField
                fullWidth
                label="Last Name"
                name="LastName"
                placeholder="Enter your last name"
                value={values.LastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.LastName && !!errors.LastName}
                helperText={touched.LastName && errors.LastName}
              />
              <StyledTextField
                fullWidth
                label="Email"
                name="email"
                placeholder="Enter your email address"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
              />
              <StyledTextField
                fullWidth
                label="Mobile Number"
                name="mobile"
                placeholder="Enter your mobile number"
                value={values.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.mobile && !!errors.mobile}
                helperText={touched.mobile && errors.mobile}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                <BackButton disabled>Back</BackButton>
                <PrimaryButton type="submit" variant="contained">
                  Next
                </PrimaryButton>
              </Box>
            </Form>
          )}
        </Formik>
      )}
  
      {activeStep === 1 && (
        <Formik
          initialValues={{
            organisationName: "",
            organisationEmail: "",
            organisationLocation: "",
            numberOfEmps:"",
            Url: "",
          }}
          validationSchema={orgSchema}
          onSubmit={handleNext}
        >
          {({ errors, touched, handleChange, handleBlur, values }) => (
            <Form>
              <StyledTextField
                fullWidth
                label="Organization Name"
                name="organisationName"
                placeholder="Enter organization name"
                value={values.organisationName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.organisationName && !!errors.organisationName}
                helperText={touched.organisationName && errors.organisationName}
              />
              <StyledTextField
                fullWidth
                label="Organization Email"
                name="organisationEmail"
                placeholder="Enter organization email address"
                value={values.organisationEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.organisationEmail && !!errors.organisationEmail}
                helperText={touched.organisationEmail && errors.organisationEmail}
              />
               <StyledTextField
                fullWidth
                type='number'
                label="Number of Employees"
                name="numberOfEmps"
                placeholder="Enter number of employees"
                value={values.numberOfEmps}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.numberOfEmps && !!errors.numberOfEmps}
                helperText={touched.numberOfEmps && errors.numberOfEmps}
              />
               <StyledTextField
                fullWidth
                label="Location"
                name="organisationLocation"
                placeholder="Enter organization location"
                value={values.organisationLocation}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.organisationLocation && !!errors.organisationLocation}
                helperText={touched.organisationLocation && errors.organisationLocation}
              />
              <StyledTextField
                fullWidth
                label="Website Url"
                name="Url"
                placeholder="Enter website URL"
                value={values.Url}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Url && !!errors.Url}
                helperText={touched.Url && errors.Url}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                <BackButton onClick={handleBack}>Back</BackButton>
                <PrimaryButton type="submit" variant="contained">
                  Next
                </PrimaryButton>
              </Box>
            </Form>
          )}
        </Formik>
      )}
  
      {/* Step 3 */}
      {activeStep === 2 && (
        <Formik
          initialValues={{
            planType: "Gold",
            duration: "",
          }}
          validationSchema={priceSchema}
          onSubmit={(values)=>
            handleSubmit({...formData ,planOpted:values.planType })
          }
        >
          {({ errors, touched, handleChange, handleBlur, values, setFieldValue }) => (
            <Form>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#212529',
                }}
              >
                Select a Plan
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {priceOptions.map((option) => (
                  <PlanCard
                    key={option.type}
                    selected={values.planType === option.type}
                    onClick={() => {
                      setFieldValue("planType", option.type);
                      setFieldValue("duration", option.duration);
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: 700,
                          color: '#212529',
                        }}
                      >
                        {option.type}
                      </Typography>
                      <Radio
                        checked={values.planType === option.type}
                        onChange={() => setFieldValue("planType", option.type)}
                        value={option.type}
                        name="planType"
                        size="small"
                        sx={{
                          padding: '4px',
                          color: '#ced4da',
                          '&.Mui-checked': { color: '#60A630' },
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#60A630',
                        letterSpacing: '0.2px',
                      }}
                    >
                      ${option.price}
                    </Typography>
                    <PlanDetail>
                      {option.duration}{" "}
                      {option.duration > 1 ? "Months" : "Month"}
                    </PlanDetail>
                    <PlanDetail>Up to {option.users} users</PlanDetail>
                  </PlanCard>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 1 }}>
                <BackButton onClick={handleBack}>Back</BackButton>
                <PrimaryButton type="submit" variant="contained">
                  Register
                </PrimaryButton>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  </Box>
  
  );
};

export default RegistrationForm;
